import styles from "@/styles/buying-guide/guide-header.module.scss";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { DownOutlined } from "@ant-design/icons";

import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();
const apiDomain = publicRuntimeConfig.apiDomain;
const imageDomain = publicRuntimeConfig.imageDomain;

interface GuideItemProps {
  categoryName?: string;
  seoDescription?: string;
}

export default function GuideHeader({
  categoryName,
  seoDescription,
}: GuideItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const toggleVisibility = () => {
    setIsVisible((prevState) => !prevState);
  };

  function handleClickMenu(url: string, e: any) {
    // 阻止a标签默认行为
    e.preventDefault();

    window.location.href = `${apiDomain}/${url}`;
  }

  const categorieslist = [
    {
      name: "Holiday Deals",
      icon: "csicon-category-2975",
      link: "holiday-deals-buying-guides/",
      categoryId: 2975,
    },
    {
      name: "Appliances",
      icon: "csicon-category-2107",
      link: "appliances-buying-guides/",
      categoryId: 2107,
    },
    {
      name: "Furniture",
      icon: "csicon-category-57",
      link: "furniture-buying-guides/",
      categoryId: 57,
    },
    {
      name: "Home & Garden",
      icon: "csicon-category-147",
      link: "home-garden-buying-guides/",
      categoryId: 147,
    },
    {
      name: "Sports & Fitness",
      icon: "csicon-category-4",
      link: "sports-fitness-buying-guides/",
      categoryId: 4,
    },
    {
      name: "Pet Supplies",
      icon: "csicon-category-73",
      link: "pet-supplies-buying-guides/",
      categoryId: 73,
    },
    {
      name: "Outdoor & Leisure",
      icon: "csicon-category-334",
      link: "outdoor-leisure-buying-guides/",
      categoryId: 334,
    },
    {
      name: "Baby, Kids & Toys",
      icon: "csicon-category-2959",
      link: "baby-kids-toys-buying-guides/",
      categoryId: 2959,
    },
    {
      name: "Bedding & Bath",
      icon: "csicon-category-258",
      link: "bedding-bath-buying-guides/",
      categoryId: 258,
    },
    {
      name: "Health & Beauty",
      icon: "csicon-category-62",
      link: "health-beauty-buying-guides/",
      categoryId: 62,
    },
    {
      name: "Tools & Auto",
      icon: "csicon-category-2957",
      link: "tools-auto-buying-guides/",
      categoryId: 2957,
    },
    {
      name: "Electronics",
      icon: "csicon-category-50",
      link: "electronics-buying-guides/",
      categoryId: 50,
    },
    {
      name: "Novelties & Gifts",
      icon: "csicon-category-65",
      link: "novelties-gifts-buying-guides/",
      categoryId: 65,
    },
    {
      name: "Musical Instruments",
      icon: "csicon-category-35",
      link: "musical-instruments-buying-guides/",
      categoryId: 35,
    },
    {
      name: "Jewellery",
      icon: "csicon-category-991",
      link: "jewellery-buying-guides/",
      categoryId: 991,
    },
    {
      name: "LED Lights",
      icon: "csicon-category-3781",
      link: "led-lights-buying-guides/",
      categoryId: 3781,
    },
    {
      name: "Fashion & Bags",
      icon: "csicon-category-109",
      link: "fashion-bags-buying-guides/",
      categoryId: 109,
    },
    {
      name: "Computers & IT",
      icon: "csicon-category-70",
      link: "computers-it-buying-guides/",
      categoryId: 70,
    },
  ];

  const categoriesItems = categorieslist.map((cate) => {
    return (
      <div
        key={cate.categoryId}
        className={styles["categories-item"]}
        id={`cate${cate.categoryId}`}
      >
        <Link href={`${apiDomain}/${cate.link}`} passHref legacyBehavior>
          <a
            className={styles["item-title"]}
            onClick={(e: any) => handleClickMenu(`${cate.link}`, e)}
            rel="noopener noreferrer"
          >
            <div className={styles["title-content"]}>
              <i className={`iconfont ${cate.icon} ${styles["icon-cate"]} `} />
              <div className={styles["cate-name"]}>{cate.name}</div>
            </div>
          </a>
        </Link>
      </div>
    );
  });

  return (
    <div className={`${styles["comp-guide-header"]}`}>
      <div className={styles["toggle-show-guides"]} onClick={toggleVisibility}>
        Read about Buying Guides
        <DownOutlined
          style={{
            transition: "transform 0.3s ease", // 添加平滑过渡效果
            transform: isVisible ? "rotate(180deg)" : "rotate(0deg)", // 控制旋转
            marginLeft: "8px", // 图标与文本之间加点间距
          }}
        />
      </div>

      {isVisible ? (
        <>
          <div className={styles["buying-guides-desc"]}>
            The CrazySales <strong>Buying Guides</strong> reveals the good, the
            bad of the industry’s products and brands, assisting you in choosing
            more eco-friendly, ethical products that support the growth of
            social responsibility and ethical business as well as best for your
            budget, economical safety. Finding everything you need in your
            everyday life is stress-free with CrazySales Buying Guide. It’s full
            of great tips to help you find the best products.
          </div>
          <div className={`${styles["buying-guides-desc"]} padding-top-16`}>
            Whether you&apos;re shopping for practical products for your pets like
            &nbsp;
            <Link href="https://www.crazysales.com.au/pet-beds/" legacyBehavior>
              <a className={styles["link"]} rel="noopener noreferrer">
                dog beds
              </a>
            </Link>
            ,&nbsp;
            <Link
              href="https://www.crazysales.com.au/cat-scratching-post-cat-tree/"
              legacyBehavior
            >
              <a className={styles["link"]} rel="noopener noreferrer">
                cat trees
              </a>
            </Link>
            &nbsp;or&nbsp;
            <Link
              href="https://www.crazysales.com.au/automatic-pet-feeder/"
              legacyBehavior
            >
              <a className={styles["link"]} rel="noopener noreferrer">
                pet feeders
              </a>
            </Link>
            ,&nbsp; big items like&nbsp;
            <Link
              href="http://www.crazysales.com.au/vibration-machines/"
              legacyBehavior
            >
              <a className={styles["link"]} rel="noopener noreferrer">
                vibration machines
              </a>
            </Link>
            ,&nbsp;
            <Link
              href="https://www.crazysales.com.au/vibration-machines/"
              legacyBehavior
            >
              <a className={styles["link"]} rel="noopener noreferrer">
                kitchen appliances
              </a>
            </Link>
            &nbsp;online for you and your families or anything in between, we&apos;ll
            give you practical facts, authentic <strong>product reviews</strong>{" "}
            and tips to help choose the right products for your lifestyle. Our{" "}
            <strong>buying guides</strong> won&apos;t overwhelm you with technical
            &quot;mumbo jumbo&quot;; we make purchases simple by cutting through the
            jargon. The <strong>CrazySales Buying Guides</strong> ensure you&apos;re
            armed with all the information you need to make an informed decision
            when purchasing practical or <strong>recommended products</strong>,
            new or classic items for your home.
          </div>
        </>
      ) : null}

      <div className={styles["banner-box"]}>
        <Image
          className={styles["banner-image"]}
          alt="crazysales"
          width={411}
          height={320}
          src={`${imageDomain}/legal/buying-guide-banner.webp`}
        ></Image>

        <div className={styles["text-box"]}>
          <div className={styles["banner-title"]}>
            {categoryName || "The CrazySales"}
            <br />
            Buying Guides
          </div>
          <div className={styles["banner-desc"]}>{seoDescription}</div>
        </div>
      </div>

      <div className={styles["categories-list"]}>{categoriesItems}</div>
    </div>
  );
}
